/**
 * Gedeelde navigatie voor de admin-dashboards (Call Tracking, Per-website, Keywords, Link Command Center).
 * Slim topbar (wordmark + acties + hamburger) + side-panel met links naar alle dashboards + dark-mode-toggle.
 * Hergebruikt de design-system-klassen .topbar / .side-panel / .side-panel-action (dark-mode-klaar).
 *
 * - <DashboardNav active="..." actions={[...]} />  -> voor de simpele dashboards (eigen topbar + menu).
 * - <DashboardLinks active="..." />                -> de links-sectie alleen (hergebruikt in Call Tracking's
 *                                                     bestaande side-panel zodat alles in sync blijft).
 */
import React, { useState } from 'react';
import { Phone, Users, Search, Magnet, Menu, X } from 'lucide-react';
import { useDarkMode } from '../../hooks/useDarkMode';

export const DASHBOARDS = [
  { key: 'calltracking', href: '/admin', label: 'Call Tracking', icon: Phone },
  { key: 'website', href: '/per-website', label: 'Per website', icon: Users },
  { key: 'keywords', href: '/keywords', label: 'Keywords', icon: Search },
  { key: 'links', href: '/links', label: 'Link Command Center', icon: Magnet },
];

const NAV_CSS = `
.dn-active.side-panel-action{ box-shadow: inset 3px 0 0 var(--color-brand-500, #D02E17); font-weight: 700; }
[data-theme="dark"] .dn-active.side-panel-action{ box-shadow: inset 3px 0 0 #f2b339; color: #f2b339; }
`;

export function DashboardLinks({ active }) {
  return (
    <>
      {DASHBOARDS.map((d) => {
        const Icon = d.icon;
        const on = d.key === active;
        return (
          <a
            key={d.key}
            className={`side-panel-action${on ? ' dn-active' : ''}`}
            href={d.href}
            style={{ textDecoration: 'none' }}
            aria-current={on ? 'page' : undefined}
          >
            <Icon className="w-4 h-4" />
            <span>{d.label}</span>
          </a>
        );
      })}
    </>
  );
}

export default function DashboardNav({ active, actions = [], children, wordmark = 'Lead Magnate' }) {
  const [open, setOpen] = useState(false);
  const [isDark, setIsDark] = useDarkMode();

  return (
    <>
      <style>{NAV_CSS}</style>

      {open && (
        <>
          <div className="side-panel-backdrop" onClick={() => setOpen(false)} />
          <aside className="side-panel" role="dialog" aria-label="Navigatie">
            <button className="side-panel-close" onClick={() => setOpen(false)} title="Sluiten">
              <X className="w-4 h-4" />
            </button>
            <div className="side-panel-content">
              {children}
              <div className="side-panel-section">
                <div className="side-panel-section-title">Dashboards</div>
                <DashboardLinks active={active} />
              </div>
              <hr className="side-panel-separator" />
              <div className="side-panel-row">
                <span>Donkere modus</span>
                <label className="auto-refresh-toggle">
                  <input type="checkbox" checked={isDark} onChange={() => setIsDark((p) => !p)} />
                  <span className="toggle-slider" />
                </label>
              </div>
            </div>
          </aside>
        </>
      )}

      <header className="topbar">
        <div className="topbar-inner">
          <span className="topbar-wordmark">{wordmark}</span>
          <div className="topbar-actions">
            {actions.map((a, i) => (
              <button key={i} onClick={a.onClick} disabled={a.disabled} className="topbar-icon-btn" title={a.title}>
                {a.icon}
              </button>
            ))}
            <button onClick={() => setOpen(true)} className="topbar-icon-btn" title="Menu">
              <Menu className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>
    </>
  );
}
